import { z } from 'zod';

// ============================================================================
// VIEW SCHEMAS (for rendering)
// ============================================================================

/**
 * View block types for rendering
 */

export const ViewEditingState = z.enum(['editable', 'locked', 'computed', 'mixed']);

export const ViewEditingMetadata = z.object({
  state: ViewEditingState,
  reason: z.string().optional(),
  hasEditableOverrides: z.boolean().optional(),
});

export const ViewBlockText = z.object({
  type: z.literal('text'),
  content: z.string(),
  variant: z.enum(['normal', 'muted', 'strong']).optional(),
});

export const ViewBlockKeyValue = z.object({
  type: z.literal('kv'),
  items: z.array(
    z.object({
      label: z.string(),
      value: z.string().optional(),
      rawValue: z.unknown().optional(),
      highlight: z.boolean().optional(),
      fieldId: z.string().optional(),
      keypath: z.string().optional(),
      fieldType: z.enum(['text', 'number', 'date', 'bool', 'choice']).optional(),
      fieldFormat: z.enum(['multiline', 'email']).optional(),
      fieldOptions: z
        .array(
          z.object({
            value: z.string(),
            label: z.string().optional(),
          })
        )
        .optional(),
      fieldSource: z.string().optional(),
      isComputed: z.boolean().optional(),
      editingState: ViewEditingState.optional(),
      editingReason: z.string().optional(),
    })
  ),
});

export const ViewBlockRowGroup = z.object({
  type: z.literal('row-group'),
  rows: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      description: z.string().optional(),
      layout: z
        .object({
          columns: z.number().int().min(1).optional(),
        })
        .optional(),
      editingState: ViewEditingState.optional(),
      editingReason: z.string().optional(),
      items: z.array(
        z.object({
          label: z.string(),
          value: z.string().optional(),
          rawValue: z.unknown().optional(),
          highlight: z.boolean().optional(),
          fieldId: z.string().optional(),
          keypath: z.string().optional(),
          fieldType: z.enum(['text', 'number', 'date', 'bool', 'choice']).optional(),
          fieldFormat: z.enum(['multiline', 'email']).optional(),
          fieldOptions: z
            .array(
              z.object({
                value: z.string(),
                label: z.string().optional(),
              })
            )
            .optional(),
          fieldSource: z.string().optional(),
          isComputed: z.boolean().optional(),
          editingState: ViewEditingState.optional(),
          editingReason: z.string().optional(),
        })
      ),
    })
  ),
});

export const ViewBlockTable = z.object({
  type: z.literal('table'),
  id: z.string(),
  columns: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      fieldPath: z.string(),
      width: z.string().optional(),
      align: z.enum(['left', 'center', 'right']).optional(),
    })
  ),
  rows: z.array(
    z.object({
      id: z.string(),
      values: z.record(z.string(), z.unknown()),
      highlight: z.boolean().optional(),
    })
  ),
  rowSource: z.string(),
  emptyMessage: z.string().optional(),
  headerStyle: z.enum(['default', 'bold', 'subtle']).optional(),
});

export const ViewBlockChecklist = z.object({
  type: z.literal('checklist'),
  items: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      checked: z.boolean().optional(),
      highlight: z.boolean().optional(),
      fieldId: z.string().optional(),
      rawValue: z.unknown().optional(),
      isComputed: z.boolean().optional(),
      editingState: ViewEditingState.optional(),
      editingReason: z.string().optional(),
    })
  ),
});

export const ViewBlockSpacer = z.object({
  type: z.literal('spacer'),
  size: z.number().optional(),
});

export const ViewBlockImage = z.object({
  type: z.literal('image'),
  src: z.string().optional(),
  alt: z.string().optional(),
  maxWidthCm: z.number().optional(),
  maxHeightCm: z.number().optional(),
  align: z.enum(['left', 'center', 'right']).optional(),
  fallbackText: z.string().optional(),
});

const ViewScorecardSeries = z.object({
  id: z.string(),
  label: z.string(),
  fieldId: z.string(),
  color: z.string().optional(),
});

const ViewScorecardEntry = z.object({
  index: z.number().int(),
  date: z.string().optional(),
  values: z.record(z.string(), z.number().nullable().optional()),
});

export const ViewBlockScorecardChart = z.object({
  type: z.literal('scorecard-chart'),
  title: z.string(),
  series: z.array(ViewScorecardSeries),
  entries: z.array(ViewScorecardEntry),
  noteFieldId: z.string(),
  noteValue: z.string().optional(),
});

export type ViewBlock =
  | z.infer<typeof ViewBlockText>
  | z.infer<typeof ViewBlockKeyValue>
  | z.infer<typeof ViewBlockRowGroup>
  | z.infer<typeof ViewBlockTable>
  | z.infer<typeof ViewBlockChecklist>
  | z.infer<typeof ViewBlockSpacer>
  | z.infer<typeof ViewBlockImage>
  | z.infer<typeof ViewBlockScorecardChart>
  | {
      type: 'grid';
      columns: Array<{ width: string; blocks: ViewBlock[] }>;
    };

/**
 * View section for rendering
 */
export const ViewSection = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  comment: z.string().optional(),
  commentMedia: z
    .array(
      z
        .object({
          id: z.string(),
          name: z.string().optional(),
          dataUrl: z.string().optional(),
          storagePath: z.string().optional(),
          mimeType: z.string().optional(),
          byteSize: z.number().optional(),
          createdAt: z.string().optional(),
        })
        .refine((value) => value.dataUrl || value.storagePath, {
          message: 'View comment media requires dataUrl or storagePath.',
        })
    )
    .optional(),
  blocks: z.array(z.unknown()), // ViewBlock[] (can't reference recursive type in Zod)
  editing: ViewEditingMetadata.optional(),
});

const ReportTocSection = z.object({
  type: z.literal('section'),
  sectionId: z.string(),
  templateSectionId: z.string(),
  label: z.string(),
});

const ReportTocCollectionEntry = z.object({
  sectionId: z.string(),
  templateSectionId: z.string(),
  label: z.string(),
  unitIndex: z.number().int(),
  unitLabel: z.string().optional(),
});

const ReportTocCollection = z.object({
  type: z.literal('collection'),
  templateSectionId: z.string(),
  label: z.string(),
  entries: z.array(ReportTocCollectionEntry),
});

export const ReportTocItem = z.discriminatedUnion('type', [ReportTocSection, ReportTocCollection]);

export const ReportTocGroup = z.object({
  id: z.string(),
  label: z.string(),
  icon: z.string(),
  order: z.number().int(),
  items: z.array(ReportTocItem),
});

/**
 * Complete report view for rendering
 */
export const ReportView = z.object({
  sections: z.array(ViewSection),
  meta: z.object({
    title: z.string(),
    visitDate: z.string(),
    inspector: z.string(),
  }),
  toc: z.array(ReportTocGroup),
});

// Export types
export type ViewEditingState = z.infer<typeof ViewEditingState>;
export type ViewEditingMetadata = z.infer<typeof ViewEditingMetadata>;
export type ViewBlockImage = z.infer<typeof ViewBlockImage>;
export type ViewBlockScorecardChart = z.infer<typeof ViewBlockScorecardChart>;
export type ViewSection = z.infer<typeof ViewSection>;
export type ReportView = z.infer<typeof ReportView>;
export type ReportTocSection = z.infer<typeof ReportTocSection>;
export type ReportTocCollectionEntry = z.infer<typeof ReportTocCollectionEntry>;
export type ReportTocCollection = z.infer<typeof ReportTocCollection>;
export type ReportTocGroup = z.infer<typeof ReportTocGroup>;
export type ReportTocItem = z.infer<typeof ReportTocItem>;
